import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { listVideos } from '../api/videos';
import {
  getBillboardControl,
  getDeviceTelemetry,
  getBillboardPlaylist,
  BillboardControlState,
  type DeviceTelemetrySnapshot,
} from '../api/billboard';
import QRCode from 'react-qr-code';

interface BillboardAd {
  _id: string;
  title: string;
  description: string;
  creativeType?: 'text' | 'image' | 'video';
  fundingStatus?: 'draft' | 'unfunded' | 'funded';
  videoUrl?: string;
  imageUrl?: string;
}

type BillboardListVideosResponse =
  | BillboardAd[]
  | {
      items: BillboardAd[];
      total?: number;
    }
  | null
  | undefined;

const TELEMETRY_INTERVAL_MS = 1000;
const API = `${process.env.REACT_APP_API_BASE || 'http://localhost:5001'}/api`;

const DEFAULT_AD: BillboardAd = {
  _id: 'demo-billboard-ad',
  title: 'TELLURVISION demo loop',
  description: 'Preview what a live billboard looks like.',
  imageUrl:
    'https://interactive-examples.mdn.mozilla.net/media/cc0-videos/flower.jpg',
  videoUrl:
    'https://interactive-examples.mdn.mozilla.net/media/cc0-videos/flower.mp4',
};

const isYouTubeUrl = (url: string | undefined | null): boolean => {
  if (!url) return false;
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase();
    return host.includes('youtube.com') || host.includes('youtu.be');
  } catch {
    return false;
  }
};

const toYouTubeEmbedUrl = (url: string): string | null => {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase();

    let videoId: string | null = null;

    if (host.includes('youtube.com')) {
      if (parsed.searchParams.has('v')) {
        videoId = parsed.searchParams.get('v');
      }
    } else if (host.includes('youtu.be')) {
      videoId = parsed.pathname.replace(/^\//, '') || null;
    }

    if (!videoId) return null;

    const base = `https://www.youtube.com/embed/${encodeURIComponent(videoId)}`;
    return `${base}?autoplay=1&loop=1&mute=1&playlist=${encodeURIComponent(videoId)}`;
  } catch {
    return null;
  }
};

const buildShareBase = (): string => {
  const envBase =
    process.env.REACT_APP_SHARE_BASE ||
    process.env.REACT_APP_API_BASE ||
    (typeof window !== 'undefined' ? window.location.origin : '');

  return envBase || '';
};

const buildAdQrUrl = (adId: string | undefined | null): string | null => {
  if (!adId) return null;
  const base = buildShareBase();
  if (!base) return null;
  const normalizedBase = base.replace(/\/$/, '');
  return `${normalizedBase}/share/ad/${encodeURIComponent(adId)}?src=qr-billboard&mode=landing`;
};

const hasUsableVideo = (videoUrl: string | undefined | null): boolean =>
  typeof videoUrl === 'string' && videoUrl.trim().length > 0;

interface BillboardProps {
  simulatedSpeedMph?: number;
  onSimulatedSpeedChange?: (value: number) => void;
  showDebugOverlay?: boolean;
  fullScreen?: boolean;
  /**
   * 'preview' = admin test player, shows ALL ads for preview purposes.
   * 'live'    = real billboard, only shows ads explicitly launched via admin.
   * Defaults to 'live' for the standalone /billboard route,
   * 'preview' when embedded in admin.
   */
  mode?: 'preview' | 'live';
}

const Billboard: React.FC<BillboardProps> = ({
  simulatedSpeedMph,
  onSimulatedSpeedChange,
  showDebugOverlay = false,
  fullScreen = true,
  mode = fullScreen ? 'live' : 'preview',
}) => {
  const [searchParams] = useSearchParams();
  const deviceId = searchParams.get('deviceId') || undefined;
  const debugMode = searchParams.get('debug') === '1';
  const shouldShowDebugOverlay = showDebugOverlay || debugMode;
  const [ads, setAds] = useState<BillboardAd[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isBlanked, setIsBlanked] = useState(false);
  const [control, setControl] = useState<BillboardControlState | null>(null);
  const [localSimulatedSpeedMph, setLocalSimulatedSpeedMph] = useState<number>(35);
  const [deviceTelemetry, setDeviceTelemetry] = useState<DeviceTelemetrySnapshot | null>(null);
  const [videoError, setVideoError] = useState(false);
  const [blankReason, setBlankReason] = useState<'speed' | 'collision' | 'admin' | 'unknown' | null>(null);
  const [showQr, setShowQr] = useState(false);

  const lastSpeedMph = deviceTelemetry?.lastSpeedMph;
  const hasDeviceSpeed =
    typeof lastSpeedMph === 'number' && !Number.isNaN(lastSpeedMph);

  const effectiveSpeedMph = hasDeviceSpeed
    ? (lastSpeedMph as number)
    : simulatedSpeedMph ?? localSimulatedSpeedMph;

  const currentAd = ads[currentIndex] ?? null;

  useEffect(() => {
    setVideoError(false);
  }, [currentAd?._id]);

  useEffect(() => {
    let cancelled = false;

    const fetchAds = async () => {
      try {
        if (mode === 'live') {
          // Real billboard: only show explicitly launched ads
          const playlist = await getBillboardPlaylist(deviceId);
          if (cancelled) return;
          if (playlist.items.length) {
            setAds(
              playlist.items.map((p) => ({
                _id: p.adId,
                title: p.title,
                description: p.description,
                creativeType: p.creativeType as BillboardAd['creativeType'],
                videoUrl: p.videoUrl,
                imageUrl: p.imageUrl,
              })),
            );
          } else {
            setAds([DEFAULT_AD]);
          }
        } else {
          // Preview mode: show all ads
          const data = (await listVideos({ includeMeta: false })) as BillboardListVideosResponse;
          if (cancelled) return;
          if (Array.isArray(data)) {
            const next = (data as BillboardAd[]) || [];
            setAds(next.length ? next : [DEFAULT_AD]);
          } else if (data && !Array.isArray(data) && Array.isArray(data.items)) {
            const next = (data.items as BillboardAd[]) || [];
            setAds(next.length ? next : [DEFAULT_AD]);
          } else {
            setAds([DEFAULT_AD]);
          }
        }
      } catch {
        if (!cancelled) setAds([DEFAULT_AD]);
      }
    };

    void fetchAds();

    // In live mode, re-fetch periodically to pick up newly launched ads
    const pollId =
      mode === 'live' ? setInterval(() => void fetchAds(), 15_000) : undefined;

    return () => {
      cancelled = true;
      if (pollId) clearInterval(pollId);
    };
  }, [mode, deviceId]);

  useEffect(() => {
    let cancelled = false;

    const fetchControl = async () => {
      try {
        const data = await getBillboardControl();
        if (!cancelled) {
          setControl(data);
        }
      } catch {
        if (!cancelled) {
          setControl(null);
        }
      }
    };

    void fetchControl();
    const id = setInterval(() => {
      void fetchControl();
    }, TELEMETRY_INTERVAL_MS * 5);

    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  useEffect(() => {
    if (!deviceId) {
      setDeviceTelemetry(null);
      return;
    }

    let cancelled = false;

    const fetchTelemetry = async () => {
      try {
        const snapshot = await getDeviceTelemetry(deviceId);
        if (!cancelled) {
          setDeviceTelemetry(snapshot);
        }
      } catch {
        if (!cancelled) {
          setDeviceTelemetry(null);
        }
      }
    };

    void fetchTelemetry();
    const id = setInterval(() => {
      void fetchTelemetry();
    }, TELEMETRY_INTERVAL_MS * 5);

    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [deviceId]);

  // Derive currentIndex from wall-clock time so every Billboard instance
  // showing the same ads list stays in sync (admin preview ↔ real billboard).
  useEffect(() => {
    if (!ads.length) return;

    const intervalSeconds = control?.rotationSeconds ?? 15;
    const intervalMs = Math.max(3, intervalSeconds) * 1000;

    const sync = () => {
      const idx = Math.floor(Date.now() / intervalMs) % ads.length;
      setCurrentIndex(idx);
    };

    sync();
    const id = setInterval(sync, 1000);

    return () => clearInterval(id);
  }, [ads.length, control?.rotationSeconds]);

  // Derive QR overlay visibility from the same wall clock so it's
  // consistent across every Billboard instance.
  useEffect(() => {
    if (!currentAd) {
      setShowQr(false);
      return;
    }

    const isDefaultAd = currentAd._id === DEFAULT_AD._id;
    if (isDefaultAd) {
      setShowQr(false);
      return;
    }

    const intervalSeconds = control?.rotationSeconds ?? 15;
    const intervalMs = Math.max(3, intervalSeconds) * 1000;
    const qrLeadSeconds = Math.min(5, Math.max(2, intervalSeconds / 3));
    const qrLeadMs = qrLeadSeconds * 1000;

    const sync = () => {
      const elapsed = Date.now() % intervalMs;
      setShowQr(elapsed >= intervalMs - qrLeadMs);
    };

    sync();
    const id = setInterval(sync, 500);

    return () => {
      clearInterval(id);
    };
  }, [currentAd, control?.rotationSeconds]);

  useEffect(() => {
    if (!currentAd) return;

    let cancelled = false;

    const id = setInterval(async () => {
      if (!currentAd) return;

      const adminForceBlank = !!control?.forceBlank;
      const shouldBlankFromSpeed = effectiveSpeedMph >= (control?.speedLimitMph ?? 45);

      if (currentAd._id === DEFAULT_AD._id) {
        const shouldBlank = adminForceBlank || shouldBlankFromSpeed;
        setIsBlanked(shouldBlank);
        if (!shouldBlank) {
          setBlankReason(null);
        } else if (adminForceBlank) {
          setBlankReason('admin');
        } else {
          setBlankReason('speed');
        }
        return;
      }

      try {
        const response = await axios.post(`${API}/ads/${currentAd._id}/telemetry/analyze`, {
          frameUrl: currentAd.imageUrl,
          speedMph: effectiveSpeedMph,
        });

        if (cancelled) return;

        const shouldBlankFromTelemetry = !!response.data?.shouldBlankAd;
        const reasons: string[] = response.data?.safety?.reasons ?? [];
        const shouldBlank = adminForceBlank || shouldBlankFromSpeed || shouldBlankFromTelemetry;

        setIsBlanked(shouldBlank);

        if (!shouldBlank) {
          setBlankReason(null);
        } else if (adminForceBlank) {
          setBlankReason('admin');
        } else if (shouldBlankFromSpeed || reasons.includes('OVER_SPEED_LIMIT')) {
          setBlankReason('speed');
        } else if (
          reasons.includes('COLLISION_RISK') ||
          (!reasons.includes('OVER_SPEED_LIMIT') && reasons.includes('HIGH_RISK_SCENE'))
        ) {
          setBlankReason('collision');
        } else {
          setBlankReason('unknown');
        }
      } catch {
        if (cancelled) return;

        const shouldBlank = adminForceBlank || shouldBlankFromSpeed;
        setIsBlanked(shouldBlank);
        if (!shouldBlank) {
          setBlankReason(null);
        } else if (adminForceBlank) {
          setBlankReason('admin');
        } else {
          setBlankReason('speed');
        }
      }
    }, TELEMETRY_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [currentAd, control?.forceBlank, control?.speedLimitMph, effectiveSpeedMph]);

  const speedLimitMph = control?.speedLimitMph ?? 45;

  const handleSpeedChange = (value: number) => {
    const next = Number.isNaN(value) ? 0 : value;
    if (onSimulatedSpeedChange) {
      onSimulatedSpeedChange(next);
    }
    if (simulatedSpeedMph === undefined) {
      setLocalSimulatedSpeedMph(next);
    }
  };

  const containerBaseClasses = 'flex items-center justify-center';
  const containerSizeClasses = fullScreen ? 'h-screen w-screen' : 'h-full w-full';
  const mediaFitClasses = fullScreen ? 'h-full w-full object-contain' : 'h-full w-full object-cover';
  const fallbackImageFitClasses = fullScreen
    ? 'max-h-full max-w-full object-contain'
    : 'h-full w-full object-cover';

  const debugOverlay = (
    <div className="fixed left-4 top-4 z-20 rounded-md bg-slate-900/80 px-3 py-2 text-xs text-slate-100 shadow">
      <div className="flex items-center justify-between gap-4">
        {!deviceId && (
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-300">
              Simulated speed
            </div>
            <div className="mt-1 flex items-center gap-2">
              <input
                type="range"
                min={0}
                max={80}
                step={1}
                value={effectiveSpeedMph}
                onChange={(e) => handleSpeedChange(Number(e.target.value) || 0)}
                className="w-32 accent-emerald-400"
              />
              <span className="tabular-nums">{effectiveSpeedMph} mph</span>
            </div>
          </div>
        )}
        <div className="text-right">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-300">
            Safety limit
          </div>
          <div className="mt-1 tabular-nums">{speedLimitMph} mph</div>
          <div className="mt-1">
            <span
              className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${
                isBlanked ? 'bg-red-500 text-white' : 'bg-emerald-500 text-white'
              }`}
            >
              {isBlanked ? 'BLANKED' : 'PLAYING'}
            </span>
          </div>
        </div>
        <p className="mt-1 text-[11px] text-slate-400">
          {deviceId
            ? 'Billboard blanks when device telemetry speed exceeds the safety limit or admin forces blank.'
            : 'Billboard blanks when simulated speed exceeds the safety limit or admin forces blank.'}
        </p>
        {deviceId && (
          <p className="mt-0.5 text-[11px] text-emerald-300">
            Using live telemetry from device: <span className="font-mono">{deviceId}</span>
          </p>
        )}
      </div>
    </div>
  );

  if (!currentAd) {
    return (
      <div
        className={`${containerBaseClasses} ${containerSizeClasses} bg-black text-slate-100`}
      >
        {shouldShowDebugOverlay && debugOverlay}
        <div className="text-center">
          <p className="text-sm uppercase tracking-[0.3em] text-slate-400">
            TELLURVISION billboard
          </p>
          <p className="mt-3 text-lg font-medium text-slate-100">Loading billboard content...</p>
        </div>
      </div>
    );
  }

  if (isBlanked) {
    let title = 'EYES ON THE ROAD';
    let body =
      'Billboard content is temporarily disabled while speed is above the safety limit.';
    let subcopy =
      'If you are testing this in a browser, you can lower the simulated speed in the dashboard to resume playback.';

    if (blankReason === 'collision') {
      title = 'HAZARD AHEAD - PAY ATTENTION';
      body =
        'Billboard content is temporarily disabled because nearby traffic looks risky. Focus on driving and surrounding vehicles.';
      subcopy =
        'The system detected a high-risk scene near this vehicle. Once conditions look safer, billboard playback can resume.';
    } else if (blankReason === 'admin') {
      body = 'Billboard content has been temporarily disabled by an administrator.';
      subcopy =
        'An operator forced this screen to blank. When the override is removed, billboard playback will resume.';
    }

    return (
      <div
        className={`${containerBaseClasses} ${containerSizeClasses} bg-slate-950 text-slate-100`}
      >
        {shouldShowDebugOverlay && debugOverlay}
        <div className="text-center">
          <h1 className="text-4xl font-semibold tracking-[0.3em] text-red-400 sm:text-5xl">
            {title}
          </h1>
          <p className="mt-4 text-base text-slate-300 sm:text-xl">{body}</p>
          <p className="mt-2 text-xs text-slate-500 dark:text-slate-400 sm:text-sm">{subcopy}</p>
        </div>
      </div>
    );
  }

  const isDefaultAd = currentAd._id === DEFAULT_AD._id;
  const rawVideoUrl = currentAd.videoUrl;
  const videoSrc = typeof rawVideoUrl === 'string' ? rawVideoUrl.trim() : '';
  const isVideoCreative =
    currentAd.creativeType === 'video' ||
    (!currentAd.creativeType && hasUsableVideo(videoSrc));
  const qrUrl = !isDefaultAd ? buildAdQrUrl(currentAd._id) : null;
  const qrSizeStyle = fullScreen
    ? {
        width: 'min(80vw, 80vh)',
        height: 'min(80vw, 80vh)',
        maxWidth: 480,
        maxHeight: 480,
      }
    : {
        width: 'clamp(104px, 18vw, 136px)',
        height: 'clamp(104px, 18vw, 136px)',
        maxWidth: 136,
        maxHeight: 136,
      };

  return (
    <div className={`${containerBaseClasses} ${containerSizeClasses} bg-black`}>
      {shouldShowDebugOverlay && debugOverlay}
      <div className="relative flex h-full w-full items-center justify-center">
        <div
          className={`h-full w-full transition-opacity duration-700 ${
            showQr ? 'opacity-30' : 'opacity-100'
          }`}
        >
          {isVideoCreative && isYouTubeUrl(videoSrc) ? (
            <iframe
              key={`yt-${currentAd._id}`}
              src={toYouTubeEmbedUrl(videoSrc) ?? ''}
              title={currentAd.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className={`h-full w-full max-h-full max-w-full bg-black ${
                fullScreen ? '' : 'aspect-video'
              }`}
            />
          ) : isVideoCreative && videoError ? (
            <img
              src={currentAd.imageUrl}
              alt={currentAd.title}
              className={fallbackImageFitClasses}
            />
          ) : isVideoCreative ? (
            <video
              key={`vid-${currentAd._id}`}
              src={videoSrc}
              autoPlay
              muted
              loop
              playsInline
              poster={currentAd.imageUrl}
              className={mediaFitClasses}
              onError={() => setVideoError(true)}
            />
          ) : (
            <div className="relative h-full w-full bg-slate-950">
              {currentAd.imageUrl?.trim() ? (
                <img
                  src={currentAd.imageUrl}
                  alt={currentAd.title}
                  className={mediaFitClasses}
                />
              ) : (
                <div className="flex h-full w-full flex-col items-center justify-center bg-[radial-gradient(circle_at_top,_rgba(59,130,246,0.35),_rgba(15,23,42,0.98)_42%,_rgba(2,6,23,1)_100%)] px-8 text-center text-slate-100">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.35em] text-cyan-200/80">
                    Text creative
                  </p>
                  <h2 className="mt-4 max-w-4xl text-3xl font-semibold text-white sm:text-4xl lg:text-5xl">
                    {currentAd.title}
                  </h2>
                  {currentAd.description && (
                    <p className="mt-4 max-w-3xl text-sm text-slate-300 sm:text-base lg:text-lg">
                      {currentAd.description}
                    </p>
                  )}
                </div>
              )}
              {!fullScreen && (
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/45 to-transparent" />
              )}
            </div>
          )}
        </div>
        {showQr && qrUrl && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-black/90 text-slate-100 transition-opacity duration-700">
            <p className="mb-3 text-xs font-medium uppercase tracking-[0.25em] text-slate-300">
              {fullScreen ? 'Scan to visit this business now' : 'QR preview'}
            </p>
            <div
              className={fullScreen
                ? 'rounded-2xl bg-white dark:bg-slate-900 p-3 shadow-2xl'
                : 'relative rounded-[34px] border border-slate-500/40 bg-gradient-to-b from-slate-500 via-slate-800 to-slate-950 p-[10px] shadow-[0_28px_70px_rgba(15,23,42,0.75)] ring-1 ring-white/10'}
            >
              {!fullScreen && (
                <>
                  <div className="absolute -left-[3px] top-20 h-12 w-[4px] rounded-full bg-slate-400/50" />
                  <div className="absolute -left-[3px] top-36 h-8 w-[4px] rounded-full bg-slate-400/40" />
                  <div className="absolute -right-[3px] top-28 h-14 w-[4px] rounded-full bg-slate-300/40" />
                </>
              )}
              <div
                className={
                  fullScreen
                    ? ''
                    : 'relative overflow-hidden rounded-[26px] bg-[radial-gradient(circle_at_top,_rgba(56,189,248,0.18),_rgba(15,23,42,0.98)_42%,_rgba(2,6,23,1)_100%)] px-4 pb-4 pt-3'
                }
              >
                {!fullScreen && (
                  <>
                    <div className="absolute inset-x-0 top-0 flex justify-center pt-2.5">
                      <div className="flex h-5 w-24 items-center justify-center rounded-b-2xl bg-black/80 shadow-[0_6px_12px_rgba(0,0,0,0.45)]">
                        <span className="h-1.5 w-10 rounded-full bg-slate-700" />
                      </div>
                    </div>
                    <div className="mb-6 flex items-center justify-between text-[9px] font-medium uppercase tracking-[0.22em] text-slate-400">
                      <span>Preview</span>
                      <span>TELLURVISION</span>
                    </div>
                  </>
                )}
                {!fullScreen && (
                  <div className="mb-3 rounded-2xl border border-cyan-400/15 bg-black/30 px-3 py-2 text-center shadow-inner shadow-black/20">
                    <p className="text-[10px] uppercase tracking-[0.28em] text-cyan-200/80">
                      Scan mockup
                    </p>
                    <p className="mt-1 text-[10px] text-slate-400">
                      Admin-only compact device frame
                    </p>
                  </div>
                )}
                <div className="rounded-2xl bg-white dark:bg-slate-900 p-2.5 shadow-lg">
                  <div style={qrSizeStyle}>
                    <QRCode
                      value={qrUrl}
                      size={512}
                      style={{ width: '100%', height: '100%' }}
                      bgColor="#ffffff"
                      fgColor="#020617"
                    />
                  </div>
                </div>
              </div>
            </div>
            <p
              className={`mt-3 text-center text-[11px] text-slate-300 ${
                fullScreen ? 'max-w-xs' : 'max-w-[18rem] px-4 text-slate-400'
              }`}
            >
              {fullScreen
                ? 'Hold your phone\'s camera up to the full-screen code. The link will open the business\'s website or checkout page for this ad.'
                : 'Admin preview uses a smaller centered QR mockup. The real billboard still shows the full-screen scannable code.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Billboard;
